/**
 * Comprehensive View State Deep Linking
 * Allows sharing links that preserve all view states including:
 * - Filters (products, categories, audiences, events, features, status, tiers)
 * - Date range
 * - View toggles (compact, parking lot, collapse/nest states, timeline icons)
 * - Sorting preferences
 */

import {
  isSharedViewMode,
  isSharedViewInSearch,
  preserveSharedViewFlagInUrl,
  buildPathWithPreservedViewState,
  SHARED_VIEW_PARAM
} from './sharedViewMode';

export type GenerateViewStateDeepLinkOptions = {
  /** Mark the link as a shared view (no localStorage persistence). */
  markAsSharedView?: boolean;
};

export interface ViewStateParams {
  // Date range
  startDate?: string;
  endDate?: string;

  // Filters
  products?: string[];
  productGroups?: string[];
  categories?: string[];
  audiences?: string[];
  events?: string[];
  features?: string[];
  status?: string[];
  tiers?: string[];
  genAiFilters?: string[];
  revenueImpactFilters?: string[];
  momentTypeFilters?: string[];
  categoryProductCategories?: string[];
  categoryProductProducts?: string[];
  surfaces?: string[];

  // View toggles
  isCompact?: boolean;
  showParkingLot?: boolean;
  collapseAll?: boolean;
  unNestAll?: boolean;

  // Sorting (for milestone grid)
  sortColumn?: string;
  sortOption?: string;
  pinColumn?: string;
  pinOption?: string;

  // Built-in "My Stuff" view
  myStuff?: boolean;

  // Must Win filters
  mustWin?: boolean;
  mustWinType?: 'audience' | 'productGroup' | 'category' | 'product' | 'multi';
  mustWinValue?: string;
  mustWinLabel?: string;
  mustWinFilters?: {
    audiences?: string[];
    categories?: string[];
    products?: string[];
    productGroups?: string[];
  };

  // Search
  searchQuery?: string;

  // Filter by specific issueId values (ARM-XXX) — matches the issueId field from backend payload
  issueIds?: string[];

  // Milestone grid specific
  isMetaTableCollapsed?: boolean;
  showBetaView?: boolean;

  // Timeline view (quarterly roadmap vs monthly timeline)
  timelineViewMode?: 'quarterly' | 'monthly';
  timelineIconsVisible?: boolean;
}

/**
 * Generate a deep link URL with all view state parameters
 * @param params - The view state parameters to encode
 * @param basePath - Optional base path (defaults to current pathname)
 * @returns Full URL with query parameters
 */
export function generateViewStateDeepLink(
  params: ViewStateParams,
  basePath?: string,
  options?: GenerateViewStateDeepLinkOptions
): string {
  const url = new URL(
    window.location.origin + (basePath || window.location.pathname)
  );

  // Date range
  if (params.startDate) url.searchParams.set('startDate', params.startDate);
  if (params.endDate) url.searchParams.set('endDate', params.endDate);

  // Filters - encode arrays as comma-separated values
  if (params.products?.length) {
    url.searchParams.set('products', params.products.join(','));
  }
  if (params.productGroups?.length) {
    url.searchParams.set('productGroups', params.productGroups.join(','));
  }
  if (params.categories?.length) {
    url.searchParams.set('categories', params.categories.join(','));
  }
  if (params.audiences?.length) {
    url.searchParams.set('audiences', params.audiences.join(','));
  }
  if (params.events?.length) {
    url.searchParams.set('events', params.events.join(','));
  }
  if (params.features?.length) {
    url.searchParams.set('features', params.features.join(','));
  }
  if (params.status?.length) {
    url.searchParams.set('status', params.status.join(','));
  }
  if (params.tiers?.length) {
    url.searchParams.set('tiers', params.tiers.join(','));
  }
  if (params.genAiFilters?.length) {
    url.searchParams.set('genAi', params.genAiFilters.join(','));
  }
  if (params.revenueImpactFilters?.length) {
    url.searchParams.set(
      'revenueImpact',
      params.revenueImpactFilters.join(',')
    );
  }
  if (params.momentTypeFilters?.length) {
    url.searchParams.set('momentTypes', params.momentTypeFilters.join(','));
  }
  if (params.categoryProductCategories?.length) {
    url.searchParams.set(
      'categoryProductCategories',
      params.categoryProductCategories.join(',')
    );
  }
  if (params.categoryProductProducts?.length) {
    url.searchParams.set(
      'categoryProductProducts',
      params.categoryProductProducts.join(',')
    );
  }
  if (params.surfaces?.length) {
    url.searchParams.set('surfaces', params.surfaces.join(','));
  }

  // View toggles - only set if explicitly true/false (not default)
  if (params.isCompact !== undefined) {
    url.searchParams.set('compact', params.isCompact.toString());
  }
  if (params.showParkingLot !== undefined) {
    url.searchParams.set('parkingLot', params.showParkingLot.toString());
  }
  if (params.collapseAll !== undefined) {
    url.searchParams.set('collapseAll', params.collapseAll.toString());
  }
  if (params.unNestAll !== undefined) {
    url.searchParams.set('unNestAll', params.unNestAll.toString());
  }

  // Sorting
  if (params.sortColumn) {
    url.searchParams.set('sortColumn', params.sortColumn);
  }
  if (params.sortOption) {
    url.searchParams.set('sortOption', params.sortOption);
  }
  if (params.pinColumn) {
    url.searchParams.set('pinColumn', params.pinColumn);
  }
  if (params.pinOption) {
    url.searchParams.set('pinOption', params.pinOption);
  }

  // Built-in "My Stuff" view
  if (params.myStuff) {
    url.searchParams.set('myStuff', 'yes');
  }

  // Must Win filters
  if (params.mustWin !== undefined) {
    if (params.mustWin) {
      url.searchParams.set('mustWin', 'yes');
      if (params.mustWinType) {
        url.searchParams.set('mustWinType', params.mustWinType);
      }
      if (params.mustWinValue) {
        url.searchParams.set('mustWinValue', params.mustWinValue);
      }
      if (params.mustWinLabel) {
        url.searchParams.set('mustWinLabel', params.mustWinLabel);
      }
      if (params.mustWinFilters) {
        url.searchParams.set(
          'mustWinFilters',
          JSON.stringify(params.mustWinFilters)
        );
      }
    } else {
      url.searchParams.set('mustWin', 'no');
    }
  }

  // Search
  if (params.searchQuery) {
    url.searchParams.set('search', params.searchQuery);
  }

  // RSD filter
  if (params.issueIds?.length) {
    url.searchParams.set('issueId', params.issueIds.join(','));
  }

  // Milestone grid specific
  if (params.isMetaTableCollapsed !== undefined) {
    url.searchParams.set(
      'metaCollapsed',
      params.isMetaTableCollapsed.toString()
    );
  }
  if (params.showBetaView !== undefined) {
    url.searchParams.set('betaView', params.showBetaView.toString());
  }

  if (params.timelineViewMode) {
    url.searchParams.set('timelineView', params.timelineViewMode);
  }
  if (params.timelineIconsVisible !== undefined) {
    url.searchParams.set(
      'timelineIcons',
      params.timelineIconsVisible.toString()
    );
  }

  if (options?.markAsSharedView || isSharedViewMode()) {
    url.searchParams.set(SHARED_VIEW_PARAM, 'true');
  }

  return url.toString();
}

/**
 * Parse view state parameters from a URL search string
 */
export function parseViewStateFromSearchParams(
  search: string
): ViewStateParams {
  const searchParams = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search
  );
  const params: ViewStateParams = {};

  // Date range
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;

  // Filters - parse comma-separated values
  const products = searchParams.get('products');
  if (products) params.products = products.split(',').filter(Boolean);

  const productGroups = searchParams.get('productGroups');
  if (productGroups)
    params.productGroups = productGroups.split(',').filter(Boolean);

  const categories = searchParams.get('categories');
  if (categories) params.categories = categories.split(',').filter(Boolean);

  const audiences = searchParams.get('audiences');
  if (audiences) params.audiences = audiences.split(',').filter(Boolean);

  const events = searchParams.get('events');
  if (events) params.events = events.split(',').filter(Boolean);

  const features = searchParams.get('features');
  if (features) params.features = features.split(',').filter(Boolean);

  const status = searchParams.get('status');
  if (status) params.status = status.split(',').filter(Boolean);

  const tiers = searchParams.get('tiers');
  if (tiers) params.tiers = tiers.split(',').filter(Boolean);

  const genAi = searchParams.get('genAi');
  if (genAi) params.genAiFilters = genAi.split(',').filter(Boolean);

  const revenueImpact = searchParams.get('revenueImpact');
  if (revenueImpact)
    params.revenueImpactFilters = revenueImpact.split(',').filter(Boolean);

  const momentTypes = searchParams.get('momentTypes');
  if (momentTypes)
    params.momentTypeFilters = momentTypes.split(',').filter(Boolean);

  const categoryProductCategories = searchParams.get(
    'categoryProductCategories'
  );
  if (categoryProductCategories)
    params.categoryProductCategories = categoryProductCategories
      .split(',')
      .filter(Boolean);

  const categoryProductProducts = searchParams.get('categoryProductProducts');
  if (categoryProductProducts)
    params.categoryProductProducts = categoryProductProducts
      .split(',')
      .filter(Boolean);

  const surfaces = searchParams.get('surfaces');
  if (surfaces) params.surfaces = surfaces.split(',').filter(Boolean);

  // View toggles
  const isCompact = searchParams.get('compact');
  if (isCompact !== null) params.isCompact = isCompact === 'true';

  const showParkingLot = searchParams.get('parkingLot');
  if (showParkingLot !== null)
    params.showParkingLot = showParkingLot === 'true';

  const collapseAll = searchParams.get('collapseAll');
  if (collapseAll !== null) params.collapseAll = collapseAll === 'true';

  const unNestAll = searchParams.get('unNestAll');
  if (unNestAll !== null) params.unNestAll = unNestAll === 'true';

  // Sorting
  const sortColumn = searchParams.get('sortColumn');
  if (sortColumn) params.sortColumn = sortColumn;

  const sortOption = searchParams.get('sortOption');
  if (sortOption) params.sortOption = sortOption;

  const pinColumn = searchParams.get('pinColumn');
  if (pinColumn) params.pinColumn = pinColumn;

  const pinOption = searchParams.get('pinOption');
  if (pinOption) params.pinOption = pinOption;

  // Built-in "My Stuff" view
  const myStuff = searchParams.get('myStuff');
  if (myStuff === 'yes') params.myStuff = true;

  // Must Win filters
  const mustWin = searchParams.get('mustWin');
  if (mustWin === 'yes') {
    params.mustWin = true;
    const mustWinType = searchParams.get('mustWinType') as
      | 'audience'
      | 'productGroup'
      | 'category'
      | 'product'
      | 'multi'
      | null;
    const mustWinValue = searchParams.get('mustWinValue');
    const mustWinLabel = searchParams.get('mustWinLabel');
    const mustWinFiltersParam = searchParams.get('mustWinFilters');

    if (mustWinType) params.mustWinType = mustWinType;
    if (mustWinValue) params.mustWinValue = mustWinValue;
    if (mustWinLabel) params.mustWinLabel = mustWinLabel;

    if (mustWinFiltersParam) {
      try {
        const parsed = JSON.parse(mustWinFiltersParam);
        if (parsed && typeof parsed === 'object') {
          params.mustWinFilters = parsed;
        }
      } catch {
        // Ignore invalid mustWinFilters JSON
      }
    }
  } else if (mustWin === 'no') {
    params.mustWin = false;
  }

  // Search
  const searchQuery = searchParams.get('search');
  if (searchQuery) params.searchQuery = searchQuery;

  // Filter by specific issueId values
  const issueId = searchParams.get('issueId');
  if (issueId) params.issueIds = issueId.split(',').filter(Boolean);

  // Milestone grid specific
  const metaCollapsed = searchParams.get('metaCollapsed');
  if (metaCollapsed !== null)
    params.isMetaTableCollapsed = metaCollapsed === 'true';

  const betaView = searchParams.get('betaView');
  if (betaView !== null) params.showBetaView = betaView === 'true';

  const timelineView = searchParams.get('timelineView');
  if (timelineView === 'monthly' || timelineView === 'quarterly') {
    params.timelineViewMode = timelineView;
  }

  const timelineIcons = searchParams.get('timelineIcons');
  if (timelineIcons !== null) {
    params.timelineIconsVisible = timelineIcons === 'true';
  }

  return params;
}

/**
 * Parse view state parameters from the current browser URL
 * @returns ViewStateParams object with all detected parameters
 */
export function parseViewStateFromUrl(): ViewStateParams {
  return parseViewStateFromSearchParams(window.location.search);
}

/**
 * Normalize pathname + search for stable saved-view URL comparison
 */
export function normalizeViewLocation(
  pathname: string,
  search: string
): string {
  const params = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search
  );
  const sorted = Array.from(params.entries()).sort(([a], [b]) =>
    a.localeCompare(b)
  );
  const normalizedSearch = new URLSearchParams(sorted).toString();
  const normalizedPath = pathname.replace(/\/+$/, '') || '/';
  return normalizedSearch
    ? `${normalizedPath}?${normalizedSearch}`
    : normalizedPath;
}

export function isMilestonesRoute(pathname: string): boolean {
  const normalizedPath = pathname.replace(/\/+$/, '') || '/';
  return normalizedPath === '/milestones';
}

/**
 * Build a navigation target for shared-view tabs using live Redux state when
 * available, so route switches keep the user's in-session filter edits.
 */
export function buildSharedViewNavigationPath(
  targetPathname: string,
  currentSearch: string,
  viewState?: ViewStateParams | null
): string {
  const normalizedPath = targetPathname.replace(/\/+$/, '') || '/';

  if (!isSharedViewMode() && !isSharedViewInSearch(currentSearch)) {
    return normalizedPath;
  }

  if (viewState) {
    const link = generateViewStateDeepLink(viewState, normalizedPath, {
      markAsSharedView: true
    });
    const { pathname, search } = new URL(link);
    return `${pathname}${search}`;
  }

  return buildPathWithPreservedViewState(normalizedPath, currentSearch);
}

export function isRoadmapRoute(pathname: string): boolean {
  const normalizedPath = pathname.replace(/\/+$/, '') || '/';
  return (
    normalizedPath === '/' ||
    normalizedPath === '/roadmap' ||
    normalizedPath.startsWith('/moments')
  );
}

/**
 * Check if URL contains any view state parameters
 * @returns true if URL has view state params, false otherwise
 */
export function hasViewStateInUrl(): boolean {
  const params = parseViewStateFromUrl();
  return Object.keys(params).length > 0;
}

/**
 * Copy current view state as a deep link to clipboard
 * @param params - The view state parameters
 * @returns Promise that resolves when copied successfully
 */
export async function copyViewStateDeepLink(
  params: ViewStateParams
): Promise<void> {
  const link = generateViewStateDeepLink(params);
  await navigator.clipboard.writeText(link);
}

/**
 * Update URL with current view state without page reload
 * @param params - The view state parameters to set
 * @param replace - If true, replaces current history entry instead of pushing new one
 */
export function updateUrlWithViewState(
  params: ViewStateParams,
  replace: boolean = true
): void {
  const url = preserveSharedViewFlagInUrl(generateViewStateDeepLink(params));
  if (replace) {
    window.history.replaceState({}, '', url);
  } else {
    window.history.pushState({}, '', url);
  }
}

/**
 * Clear all view state parameters from URL without page reload
 * Preserves slideout-related params (moment, phaseId, etc.)
 */
export function clearViewStateFromUrl(): void {
  const url = new URL(window.location.href);
  const searchParams = new URLSearchParams(url.search);

  // List of view state params to remove
  const viewStateParams = [
    'startDate',
    'endDate',
    'products',
    'productGroups',
    'categories',
    'audiences',
    'events',
    'features',
    'status',
    'tiers',
    'genAi',
    'revenueImpact',
    'momentTypes',
    'mustWinFilter',
    'categoryProductCategories',
    'categoryProductProducts',
    'surfaces',
    'compact',
    'parkingLot',
    'collapseAll',
    'unNestAll',
    'sortColumn',
    'sortOption',
    'pinColumn',
    'pinOption',
    'myStuff',
    'mustWin',
    'mustWinType',
    'mustWinValue',
    'mustWinLabel',
    'mustWinFilters',
    'search',
    'issueId',
    'metaCollapsed',
    'betaView',
    'timelineView',
    'timelineIcons'
  ];

  // Remove only view state params, preserve others (like moment, phaseId)
  viewStateParams.forEach(param => searchParams.delete(param));

  // Update URL
  url.search = searchParams.toString();
  window.history.replaceState({}, '', url.toString());
}

/**
 * Merge URL view state with local preferences
 * URL parameters take precedence over local preferences
 * @param localPreferences - Local preferences object
 * @param urlParams - Parsed URL parameters
 * @returns Merged view state with URL params taking precedence
 */
export function mergeViewStateWithPreferences<T extends Record<string, any>>(
  localPreferences: T,
  urlParams: Partial<T>
): T {
  return {
    ...localPreferences,
    ...urlParams
  };
}
